/**
 * Planar reflection probe for the ocean (Layer-4 of the water brief — the
 * deferred "render-target pre-pass" the shader's `sky` hook was built for).
 *
 * The near ocean patch is locally FLAT (a tangent plane at the viewer's
 * sub-point), so a single planar reflection across that plane is accurate and
 * cheap — far better than SSR (misses off-screen) or a per-frame cubemap (misses
 * dynamic players/terrain). We mirror the camera across the water plane, render
 * the scene from that virtual camera into a HALF-RES target, and the water
 * shader samples it projectively (distorted by the wave slope for ripples).
 *
 * Best-practice performance: half-resolution target, OBLIQUE near-plane clip so
 * nothing under the water is drawn (no wasted overdraw, no leak), the caller
 * hides the water surfaces so there's no self-reflection, and the whole pass is
 * skipped unless the animated patch is actually on screen (its altitude gate).
 *
 * The virtual camera is rebuilt as a normal position+lookAt+up camera (NOT a
 * negative-determinant mirror matrix), so triangle winding is preserved and
 * back-face culling stays correct. Everything is render space (origin-relative),
 * matching the rest of the floating-origin pipeline.
 */
import * as THREE from "three";
import { RenderCellPass } from '../../engine/renderCells';
import { ScenePose } from '../../engine/scenePose';

export class WaterReflection {
  private readonly cells = new RenderCellPass();
  private readonly pose = new ScenePose();
  readonly renderTarget: THREE.WebGLRenderTarget;
  /** bias · proj · viewInverse of the virtual camera — projects a render-space
   *  world position straight to reflection-texture UV. Mutated in place each
   *  render(), so a uniform pointed at this object updates for free. */
  readonly textureMatrix = new THREE.Matrix4();

  private readonly virtualCamera = new THREE.PerspectiveCamera();
  private readonly clipBias = 0.0028; // nudge the oblique plane to kill a thin seam at the waterline

  // scratch — no per-frame allocation
  private readonly reflWorld = new THREE.Vector3();
  private readonly camWorld = new THREE.Vector3();
  private readonly normal = new THREE.Vector3();
  private readonly view = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly rot = new THREE.Matrix4();
  private readonly plane = new THREE.Plane();
  private readonly clip = new THREE.Vector4();
  private readonly q = new THREE.Vector4();
  private readonly prevClear = new THREE.Color();

  constructor(width: number, height: number) {
    this.renderTarget = new THREE.WebGLRenderTarget(Math.max(2, width), Math.max(2, height), {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      type: THREE.UnsignedByteType, // LDR reflection; cheap + the wave distortion hides banding
      depthBuffer: true,
      stencilBuffer: false,
      // No MSAA: it was added for clean reflected edges, but the sampler distorts
      // the image by the wave slope anyway — paying resolve bandwidth to anti-alias
      // edges we then deliberately wobble. (The client is GPU-fill-bound; see the
      // perf profile note in the repo memory.)
    });
    this.renderTarget.texture.generateMipmaps = false;
    // Keep the target LINEAR (default): the water shader works in linear and is
    // sRGB-encoded once on final output, so a linear sample stays consistent.
  }

  setSize(width: number, height: number): void {
    this.renderTarget.setSize(Math.max(2, width), Math.max(2, height));
  }

  get texture(): THREE.Texture {
    return this.renderTarget.texture;
  }

  /**
   * Render `scene` reflected across the plane (point + unit normal, render space)
   * into the target. Returns false (and renders nothing) if the camera is on the
   * back side of the plane — underwater / under the surface — where a planar
   * reflection is meaningless; the caller should drop the reflection then.
   *
   * `fovScale` > 1 renders the mirror through a WIDER frustum than the main
   * camera. It matters more than it looks: a point ON the plane projects to the
   * SAME pixel through the mirrored camera as through the real one, so at scale 1
   * the reflection texture's border sits exactly on the screen's border — and any
   * fade the sampler applies at that border draws a frame around the viewport (see
   * the note on the ocean's `inB`). Scaling the frustum pushes the texture border
   * off-screen, so on-screen water always samples the mirror's interior.
   */
  render(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    point: THREE.Vector3,
    planeNormal: THREE.Vector3,
    clearColor: THREE.Color,
    fovScale = 1,
  ): boolean {
    this.reflWorld.copy(point);
    this.camWorld.setFromMatrixPosition(camera.matrixWorld);
    this.normal.copy(planeNormal).normalize();

    // Camera behind the mirror (below the surface) → no reflection.
    this.view.subVectors(this.reflWorld, this.camWorld);
    if (this.view.dot(this.normal) > 0) return false;

    // Mirror the eye position across the plane.
    this.view.reflect(this.normal).negate().add(this.reflWorld);

    // Mirror the look-at point across the plane.
    this.rot.extractRotation(camera.matrixWorld);
    this.look.set(0, 0, -1).applyMatrix4(this.rot).add(this.camWorld);
    this.target.subVectors(this.reflWorld, this.look);
    this.target.reflect(this.normal).negate().add(this.reflWorld);

    // Build the virtual camera as a proper position/up/lookAt camera (positive
    // determinant → winding preserved → back-face culling correct).
    this.virtualCamera.position.copy(this.view);
    this.virtualCamera.up.set(0, 1, 0).applyMatrix4(this.rot).reflect(this.normal);
    this.virtualCamera.lookAt(this.target);
    this.virtualCamera.near = camera.near;
    this.virtualCamera.far = camera.far;
    // Widen by fovScale on the TANGENT (aspect unchanged widens horizontally by
    // the same factor), so the screen maps to the mirror's central 1/fovScale.
    this.virtualCamera.fov =
      fovScale === 1
        ? camera.fov
        : THREE.MathUtils.radToDeg(
            2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) * fovScale),
          );
    this.virtualCamera.aspect = camera.aspect;
    this.virtualCamera.updateProjectionMatrix();
    this.virtualCamera.updateMatrixWorld(); // Camera.updateMatrixWorld also refreshes matrixWorldInverse

    // textureMatrix = bias · proj · viewInverse.
    this.textureMatrix.set(0.5, 0, 0, 0.5, 0, 0.5, 0, 0.5, 0, 0, 0.5, 0.5, 0, 0, 0, 1);
    this.textureMatrix.multiply(this.virtualCamera.projectionMatrix);
    this.textureMatrix.multiply(this.virtualCamera.matrixWorldInverse);

    // Oblique near-plane clip: push the near plane onto the water plane so nothing
    // below the surface is rendered into the reflection (geometric clip on z/w —
    // works fine under the logarithmic depth buffer, which only re-encodes depth).
    this.plane.setFromNormalAndCoplanarPoint(this.normal, this.reflWorld);
    this.plane.applyMatrix4(this.virtualCamera.matrixWorldInverse);
    this.clip.set(this.plane.normal.x, this.plane.normal.y, this.plane.normal.z, this.plane.constant);
    const p = this.virtualCamera.projectionMatrix.elements;
    this.q.x = (Math.sign(this.clip.x) + p[8]) / p[0];
    this.q.y = (Math.sign(this.clip.y) + p[9]) / p[5];
    this.q.z = -1.0;
    this.q.w = (1.0 + p[10]) / p[14];
    this.clip.multiplyScalar(2.0 / this.clip.dot(this.q));
    p[2] = this.clip.x;
    p[6] = this.clip.y;
    p[10] = this.clip.z + 1.0 - this.clipBias;
    p[14] = this.clip.w;

    // Render the scene from the virtual camera into the half-res target.
    const prevRT = renderer.getRenderTarget();
    const prevAlpha = renderer.getClearAlpha();
    renderer.getClearColor(this.prevClear);
    const prevShadowAuto = renderer.shadowMap.autoUpdate;
    const prevShadowNeeds = renderer.shadowMap.needsUpdate;
    this.pose.begin(scene);
    try {
      renderer.shadowMap.autoUpdate = false;
      renderer.shadowMap.needsUpdate = false;
      renderer.setRenderTarget(this.renderTarget);
      renderer.setClearColor(clearColor, 1);
      renderer.clear();
      this.cells.begin(scene, this.virtualCamera);
      renderer.render(scene, this.virtualCamera);
    } finally {
      this.cells.end();
      this.pose.end(scene);
      renderer.setRenderTarget(prevRT);
      renderer.setClearColor(this.prevClear, prevAlpha);
      renderer.shadowMap.autoUpdate = prevShadowAuto;
      renderer.shadowMap.needsUpdate = prevShadowNeeds;
    }
    return true;
  }

  dispose(): void {
    this.cells.dispose();
    this.renderTarget.dispose();
  }
}
